'use client';

import { useState } from 'react';
import { ComponentDesignType, ComponentType, GetPageComponentViewModel } from '@/types/cml-api';
import { sendCtaClickEvent } from '@/features/analytics/datalayer/datalayer';
import { AppNextLink } from '@/features/app-next-link';
import { IconArrowSquareLeft } from '@/features/icons';
import { useMobileDetect } from '@/features/mobile-detect-context/mobile-detect-provider';
import TabBar from '@/features/tab-bar/tab-bar';
import { TabBarItem, TabSize, TabType } from '@/features/tab-bar/types';
import { Webengage } from '@/features/webengage';
import { cn } from '@/utils/cn';
import PinkBox from './pink-box';
import PinkboxSkeleton from './pinkbox-skeleton';
import ProductsListCarousel from './products-list-carousel';
import { deviceTypeClassGenerator } from '../../_utils/device-type-handlers';
import { IsLoadingLazyProps } from '../../type';
import CustomAnimatedTitle from '../goodbye-summer-landing/custom-animated-title';
interface Props extends IsLoadingLazyProps {
  data: GetPageComponentViewModel;
  isPersonalized?: boolean;
  isGoodbyeSummerLanding?: boolean;
}
function ProductsList({
  data,
  isLazy,
  isPersonalized,
  isGoodbyeSummerLanding
}: Props) {
  const isPinkBox = data.designType === ComponentDesignType.PinkBox;
  const [carouselIsLoaded, setCarouselIsLoaded] = useState(false);
  const {
    isMobile
  } = useMobileDetect();
  const showLinkInHeader = data?.items && data?.items.length < 2 && data?.showTitleAndSubtitle && !isPinkBox;
  const isDefaultProductList = !Object.values(ComponentDesignType).includes(data.designType as ComponentDesignType);
  const solidColor = data.designType === ComponentDesignType.SolidColor ? data.backgroundColor : '';
  const backgroundImage =
  // eslint-disable-next-line no-nested-ternary
  data.designType === ComponentDesignType.Image ? isMobile ? data.mobileImageUrl : data.desktopImageUrl : '';
  const currentData = data.items?.at(0);
  const destinationUrl = currentData?.link ?? '';
  let containerClasses = '';
  if (isPinkBox && carouselIsLoaded) {
    containerClasses = 'bg-primary-main p-0 pl-2 lg:p-2 lg:pb-4 lg:pl-4';
  } else if (solidColor) {
    // eslint-disable-next-line no-nested-ternary
    containerClasses = showLinkInHeader ? 'p-2 pl-0 lg:p-4' : data?.showBannerInList ? 'p-2 lg:p-4' : 'p-2 lg:p-4';
  } else if (backgroundImage) {
    // eslint-disable-next-line no-nested-ternary
    containerClasses = showLinkInHeader ? 'bg-cover bg-center p-2 pl-0 lg:p-4' : data?.showBannerInList ? 'bg-cover bg-center p-2 lg:p-4' : 'bg-cover bg-center p-2 lg:p-4';
  }
  const handleClick = (link?: string | null) => {
    const componentId = data.id ?? '';
    const elementIndex = 0;
    const itemId = data?.id ?? '';
    const destinationUrl = link ?? '';
    const image = null;
    const elementType = isPersonalized ? ComponentType.PersonalizedProductList : ComponentType.ProductList;
    sendCtaClickEvent({
      componentId: componentId,
      elementType,
      elementName: data?.sortKey,
      elementIndex: elementIndex,
      itemID: itemId,
      destinationUrl: destinationUrl,
      ImageUrl: image,
      elementGroupName: `${data?.title || ''}-1`
    });
    Webengage.trackCtaAction({
      component_id: componentId,
      banner_type: elementType,
      banner_name: data?.sortKey ?? '',
      banner_index: elementIndex,
      url_destination: destinationUrl,
      item_id: itemId,
      ...(image ? {
        image_url: image
      } : {})
    });
  };
  const TabHeader = () => {
    return <>
                {isGoodbyeSummerLanding && <CustomAnimatedTitle title={data.title || ''} />}
                {!isGoodbyeSummerLanding && !isPinkBox && data.showTitleAndSubtitle && <div className='flex w-full items-center'>
                        <div className='flex-1'>
                            <span className={cn('font-bold', isPinkBox ? 'text-body-16 text-text-white lg:text-title-18' : 'text-body-14 lg:text-body-16')}>
                                {data.title}
                            </span>
                            {data.subTitle && <p className='font-regular my-1 text-caption-12 text-text-mediumGray lg:my-2 lg:text-body-14'>
                                    {data.subTitle}
                                </p>}
                        </div>

                        {currentData?.link && showLinkInHeader && <AppNextLink onClick={() => handleClick(destinationUrl)} href={currentData?.link ?? ''} className='flex items-center gap-1.5 text-body-14 font-medium text-primary-main lg:font-bold'>
                                <span className='whitespace-nowrap'> مشاهده همه </span>
                                {isMobile && <IconArrowSquareLeft fontSize={20} />}
                            </AppNextLink>}
                    </div>}

                {isPinkBox && isMobile && <div className='w-full px-0'>
                        {carouselIsLoaded ? <PinkBox deactivationCountdownSecs={data?.deactivationCountdownSecs || 0} id={currentData?.id} componentId={data?.id} href={currentData?.link || ''} elementName={data.sortKey} /> : <PinkboxSkeleton />}
                    </div>}
            </>;
  };
  const tabs: TabBarItem[] = data.items?.map(item => ({
    header: item.title,
    body: <div className={cn({
      'pb-2 lg:pb-0': isPinkBox
    }, {
      '!px-0': !isPinkBox
    }, deviceTypeClassGenerator(data.deviceTypes || ''))}>
                    <ProductsListCarousel isPinkBox={isPinkBox} data={item} isLazy={isLazy} carouselIsLoaded={carouselIsLoaded} setCarouselIsLoaded={setCarouselIsLoaded} componentData={data} showLinkInHeader={showLinkInHeader ?? false} isPersonalized={isPersonalized} isGoodbyeSummerLanding={isGoodbyeSummerLanding} />
                </div>
  })) || [];
  return <div className='homepage-container' data-test='products-list' data-sentry-component="ProductsList" data-sentry-source-file="products-list.tsx">
            {!isPinkBox && <TabHeader />}
            <div className={cn('mt-2 rounded', containerClasses)} style={
    // eslint-disable-next-line no-nested-ternary
    solidColor ? {
      backgroundColor: solidColor
    } : backgroundImage ? {
      backgroundImage: `url(${backgroundImage})`
    } : {}}>
                {isPinkBox && <TabHeader />}
                <TabBar type={isPinkBox ? TabType.PinkBox : TabType.Normal} isProductsHeader items={tabs} size={TabSize.Medium} headClassName={cn('mb-2 bg-white rounded px-1 w-full overflow-hidden', {
        'border-b border-border-gray-light rounded-none': isDefaultProductList
      })} tabClassName='shadow-none border-none rounded-none' hasLeftPadding={!isDefaultProductList} data-sentry-element="TabBar" data-sentry-source-file="products-list.tsx" />
            </div>
        </div>;
}
export default ProductsList;